import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  isSameMonth,
  startOfToday,
} from "date-fns";
import { useSelector } from "react-redux";
import useRepCalls from "../../hooks/useRepCalls";
import CallsPanel from "./CallsPanel";
import LocationsMap from "../elements/LocationsMap";
import { getId } from "../../utils/idHelpers";
import useLocalization from "../../hooks/useLocalization";

const RepCalendar = ({ repId }) => {
  const language = useSelector((state) => state.authentication.language);
  const isFrench = String(language || "")
    .toLowerCase()
    .startsWith("fr");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mapDate, setMapDate] = useState(null);
  const [mapTitle, setMapTitle] = useState("");
  const { callsByDate, isLoading, fetchCalls } = useRepCalls(repId);
  const { getLabel, getMonthFullName, getMonthShortName, calendarLists } =
    useLocalization();
  const weekdayLabels = (
    calendarLists?.weekdays || [
      { short: "Sun" },
      { short: "Mon" },
      { short: "Tue" },
      { short: "Wed" },
      { short: "Thu" },
      { short: "Fri" },
      { short: "Sat" },
    ]
  ).map((day) => day.short || day.name);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  // Group calendar days by week
  const weeks = useMemo(() => {
    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before month starts
    const firstDayOfWeek = monthStart.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }

    // Add days of month
    calendarDays.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    // Add empty cells for days after month ends
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push([...currentWeek]);
    }

    return weeks;
  }, [calendarDays, monthStart]);

  const getCallsForDate = (day) => {
    if (!day) return [];
    const dateStr = format(day, "yyyy-MM-dd");
    return callsByDate[dateStr] || [];
  };

  const getLocalizedName = (entity, fallbackName, fallbackFrenchName) => {
    const defaultName = fallbackName || "";
    if (!entity || typeof entity !== "object") {
      return isFrench
        ? fallbackFrenchName || defaultName
        : defaultName || fallbackFrenchName || "";
    }

    if (isFrench) {
      return (
        entity.name_fr || fallbackFrenchName || entity.name || defaultName || ""
      );
    }

    return (
      entity.name || defaultName || entity.name_fr || fallbackFrenchName || ""
    );
  };

  const formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "string") return address;

    const parts = [
      address.streetAddress,
      address.city,
      address.province,
      address.postal,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getCoordinateValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getCoordinatesFromAddress = (address) => {
    if (!address || typeof address !== "object") {
      return { latitude: null, longitude: null };
    }

    const coordinateArray =
      address.coordinates || address.coords || address.geoCoordinates;
    if (Array.isArray(coordinateArray) && coordinateArray.length >= 2) {
      const [lng, lat] = coordinateArray;
      return {
        latitude: getCoordinateValue(lat),
        longitude: getCoordinateValue(lng),
      };
    }

    const latitude =
      address.latitude ||
      address.lat ||
      address.geo?.latitude ||
      address.geo?.lat ||
      address.location?.latitude ||
      address.location?.lat ||
      address.coordinates?.latitude ||
      address.coordinates?.lat;
    const longitude =
      address.longitude ||
      address.lng ||
      address.lon ||
      address.geo?.longitude ||
      address.geo?.lng ||
      address.geo?.lon ||
      address.location?.longitude ||
      address.location?.lng ||
      address.location?.lon ||
      address.coordinates?.longitude ||
      address.coordinates?.lng ||
      address.coordinates?.lon;

    return {
      latitude: getCoordinateValue(latitude),
      longitude: getCoordinateValue(longitude),
    };
  };

  const getLocationsForDate = (day) => {
    const calls = getCallsForDate(day);
    const grouped = {};

    calls.forEach((call) => {
      const locationId = getId(call.location) || call.locationId;
      if (!locationId) return;

      const locationName = call.location?.name || call.locationName;
      const locationNumber =
        call.location?.locationNumber || call.locationNumber;
      const rawAddress = call.location?.address || call.locationAddress;
      const locationAddress = formatAddress(rawAddress);
      const addressCoordinates = getCoordinatesFromAddress(rawAddress);
      const rawLatitude =
        call.location?.latitude ||
        call.location?.lat ||
        addressCoordinates.latitude;
      const rawLongitude =
        call.location?.longitude ||
        call.location?.lng ||
        call.location?.lon ||
        addressCoordinates.longitude;

      if (!grouped[locationId]) {
        grouped[locationId] = {
          id: locationId,
          name: locationName,
          number: locationNumber,
          address: locationAddress,
          latitude: getCoordinateValue(rawLatitude),
          longitude: getCoordinateValue(rawLongitude),
          calls: [],
        };
      }

      grouped[locationId].calls.push({
        id: getId(call) || call.id,
        scheduleDate: call.scheduledDate || call.scheduleDate || call.yyyymmdd,
        projectName: call.project?.name || call.projectName,
        status: call.callStatus || call.status,
      });
    });

    return Object.values(grouped);
  };

  const projectsByDate = useMemo(() => {
    const byDate = {};

    Object.keys(callsByDate || {}).forEach((dateKey) => {
      const calls = Array.isArray(callsByDate[dateKey])
        ? callsByDate[dateKey]
        : [];
      const projectsMap = new Map();

      calls.forEach((call) => {
        const projectKey = call.projectId || call.project?.id;
        if (!projectKey) return;

        if (!projectsMap.has(projectKey)) {
          projectsMap.set(projectKey, {
            projectId: projectKey,
            projectName: getLocalizedName(
              call.project,
              call.projectName,
              call.projectName_fr,
            ),
            statuses: [],
          });
        }

        projectsMap.get(projectKey).statuses.push(call.callStatus);
      });

      byDate[dateKey] = Array.from(projectsMap.values());
    });

    return byDate;
  }, [callsByDate, isFrench]);

  const getProjectsForDate = (day) => {
    if (!day) return [];
    const dateStr = format(day, "yyyy-MM-dd");
    return projectsByDate[dateStr] || [];
  };

  const getStatusDots = (statuses) => {
    const statusCounts = {
      complete: 0,
      incomplete: 0,
      future: 0,
    };

    statuses.forEach((status) => {
      statusCounts[status || "future"]++;
    });

    return statusCounts;
  };

  const handlePrevMonth = () => setCurrentMonth(addMonths(currentMonth, -1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(startOfToday());

  const handleSelectDate = (day) => {
    if (day) {
      setSelectedDate(day);
      setIsPanelOpen(true);
    }
  };

  const handleOpenMap = (day) => {
    if (!day) return;
    setMapDate(day);
    const monthLabel = getMonthShortName(day.getMonth()) || format(day, "MMM");
    const dayLabel = format(day, "d");
    const yearLabel = format(day, "yyyy");
    setMapTitle(
      `${getLabel("mapFor")} ${monthLabel} ${dayLabel}, ${yearLabel}`,
    );
    setIsMapOpen(true);
  };

  const handleCloseMap = () => {
    setIsMapOpen(false);
    setMapDate(null);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setSelectedDate(null);
  };

  const handleRefreshCalendar = () => {
    fetchCalls();
    // Keep panel open but update calls
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with Month Navigation */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 0, flexShrink: 0 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600 }}
        >
          {`${getMonthFullName(currentMonth.getMonth()) || format(currentMonth, "MMMM")} ${format(currentMonth, "yyyy")}`}
        </Typography>
        <Stack
          direction="row"
          gap={1}
        >
          <Button
            startIcon={<ChevronLeftIcon />}
            onClick={handlePrevMonth}
            variant="outlined"
            size="small"
          >
            {getLabel("prev")}
          </Button>
          <Button
            onClick={handleToday}
            variant="outlined"
            size="small"
          >
            {getLabel("today")}
          </Button>
          <Button
            endIcon={<ChevronRightIcon />}
            onClick={handleNextMonth}
            variant="outlined"
            size="small"
          >
            {getLabel("next")}
          </Button>
        </Stack>
      </Stack>

      {/* Weekday Headers */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          marginBottom: 1,
          flexShrink: 0,
        }}
      >
        {weekdayLabels.map((day) => (
          <Box key={day}>
            <Typography
              align="center"
              sx={{ fontWeight: 600, color: "#666", fontSize: "0.875rem" }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: `repeat(${weeks.length}, 1fr)`,
          gap: 0.5,
          flex: 1,
          minHeight: 0,
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        {weeks.map((week, weekIdx) =>
          week.map((day, dayIdx) => {
            const projects = getProjectsForDate(day);
            const callCount = getCallsForDate(day).length;
            const isToday =
              day &&
              isSameMonth(day, new Date()) &&
              format(day, "d") === format(new Date(), "d");

            return (
              <Box
                key={`${weekIdx}-${dayIdx}`}
                sx={{ minHeight: 0, height: "100%" }}
              >
                <Card
                  onClick={() => handleSelectDate(day)}
                  sx={{
                    padding: 1,
                    height: "100%",
                    overflow: "auto",
                    cursor: day ? "pointer" : "default",
                    backgroundColor: isToday ? "#f0f7ff" : "#fafafa",
                    border: isToday ? "2px solid #1976d2" : "1px solid #e0e0e0",
                    borderRadius: 1,
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s",
                    "&:hover": day
                      ? { boxShadow: 2, backgroundColor: "#f5f5f5" }
                      : {},
                  }}
                >
                  {day ? (
                    <>
                      {/* Day Number */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: isToday ? "#1976d2" : "#333",
                          }}
                        >
                          {format(day, "d")}
                        </Typography>

                        {callCount > 0 && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenMap(day);
                            }}
                            sx={{ ml: 1 }}
                            aria-label={getLabel("openMap")}
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Call Count */}
                      {callCount > 0 && (
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#999",
                            marginTop: 0.5,
                          }}
                        >
                          {callCount} {getLabel("calls")}
                        </Typography>
                      )}

                      {/* Projects and Status Dots */}
                      <Box
                        sx={{
                          marginTop: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {projects.map((project) => {
                          const statusDots = getStatusDots(project.statuses);

                          return (
                            <Box key={project.projectId}>
                              {/* Project Name */}
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#333",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {project.projectName}
                              </Typography>

                              {/* Status Dots */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.25,
                                  marginTop: 0.25,
                                  flexWrap: "wrap",
                                }}
                              >
                                {/* Complete dots */}
                                {Array.from({
                                  length: statusDots.complete,
                                }).map((_, idx) => (
                                  <Box
                                    key={`complete-${idx}`}
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: "#4caf50",
                                    }}
                                  />
                                ))}
                                {/* Incomplete dots */}
                                {Array.from({
                                  length: statusDots.incomplete,
                                }).map((_, idx) => (
                                  <Box
                                    key={`incomplete-${idx}`}
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: "#f44336",
                                    }}
                                  />
                                ))}
                                {/* Future dots */}
                                {Array.from({ length: statusDots.future }).map(
                                  (_, idx) => (
                                    <Box
                                      key={`future-${idx}`}
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: "#9e9e9e",
                                      }}
                                    />
                                  ),
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  ) : null}
                </Card>
              </Box>
            );
          }),
        )}
      </Box>

      {/* Calls Panel Slider */}
      {isPanelOpen && selectedDate && (
        <CallsPanel
          date={selectedDate}
          calls={getCallsForDate(selectedDate)}
          onClose={handleClosePanel}
          onRefresh={handleRefreshCalendar}
        />
      )}

      <Dialog
        open={isMapOpen}
        onClose={handleCloseMap}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {mapTitle}
          <IconButton
            onClick={handleCloseMap}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label={getLabel("closeMap")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: "70vh", p: 2 }}>
          <Box sx={{ height: "100%" }}>
            <LocationsMap
              locations={mapDate ? getLocationsForDate(mapDate) : []}
              selectedLocationId={null}
              hoveredLocationId={null}
              onSelectLocation={() => {}}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RepCalendar;
